import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { api, fileUrl } from "@/api";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";
import Portrait from "@/components/Portrait";
import NotFound from "@/pages/NotFound";

export default function BrandDetail() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMissing(false);
    setBrand(null);
    api.get(`/brands/by-slug/${slug}`)
      .then((r) => setBrand(r.data))
      .catch(() => setMissing(true));
    api.get("/leadership").then((r) => setLeaders(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (missing) return <NotFound />;
  if (!brand) return <div className="min-h-screen bg-navy-950" data-testid="brand-loading" />;

  const accent = brand.brandColor || "#D4AF37";
  const brandLeaders = leaders.filter(
    (l) => l.brand && l.brand.toLowerCase() === brand.brandName.toLowerCase()
  );

  return (
    <div data-testid={`brand-page-${slug}`}>
      <section className="relative overflow-hidden bg-navy-950 pt-40 pb-24 lg:pt-48" data-testid="brand-hero">
        <div className="absolute inset-0">
          <img src={fileUrl(brand.heroImage)} alt="" aria-hidden className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-navy-950/70" />
        </div>
        <div className="container-rcs relative">
          <Reveal>
            <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 text-xs text-slate-400" data-testid="breadcrumb">
              <Link to="/" className="hover:text-gold transition-colors">Home</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <Link to="/brands" className="hover:text-gold transition-colors">Our Brands</Link>
              <ChevronRight className="h-3 w-3" aria-hidden />
              <span className="text-gold">{brand.brandName}</span>
            </nav>
            <span className="inline-block rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white" style={{ backgroundColor: accent }}>
              {brand.category}
            </span>
            <h1 className="mt-6 font-serif text-4xl text-ivory sm:text-5xl lg:text-6xl">{brand.brandName}</h1>
            {brand.tagline && <p className="mt-4 text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">{brand.tagline}</p>}
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{brand.description}</p>
            <a href={brand.websiteURL} target="_blank" rel="noopener noreferrer" className="btn-gold mt-10" data-testid="brand-hero-visit-cta">
              Explore {brand.brandName} <ArrowUpRight className="h-4 w-4" aria-hidden />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="brand-purpose">
        <div className="container-rcs grid gap-16 lg:grid-cols-2">
          <div>
            <Section eyebrow="Purpose" title={`Why ${brand.brandName} exists.`} />
            {brand.purpose && (
              <Reveal delay={0.1}>
                <p className="mt-6 text-base leading-relaxed text-slate-600">{brand.purpose}</p>
              </Reveal>
            )}
            {brand.vision && (
              <Reveal delay={0.15}>
                <blockquote className="mt-8 border-l-2 pl-6 font-serif text-xl italic text-navy-900" style={{ borderColor: accent }}>
                  {brand.vision}
                </blockquote>
              </Reveal>
            )}
          </div>
          {brand.learningAreas?.length > 0 && (
            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-navy-900/10 bg-white p-8 lg:p-10" data-testid="brand-learning-areas">
                <p className="eyebrow">Learning Areas</p>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {brand.learningAreas.map((a) => (
                    <span key={a} className="rounded-full border px-4 py-1.5 text-xs font-semibold text-navy-900" style={{ borderColor: `${accent}55`, backgroundColor: `${accent}0d` }}>
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="border-t border-navy-900/10 bg-white py-24" data-testid="brand-leadership">
        <div className="container-rcs">
          <Section eyebrow="Brand Leadership" title={`Leadership at ${brand.brandName}.`} />
          {brandLeaders.length > 0 ? (
            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {brandLeaders.map((l, i) => (
                <Reveal key={l.id} delay={i * 0.1}>
                  <Link to={`/leadership/${l.slug}`} className="card-premium group block h-full" data-testid={`brand-leader-${l.slug}`}>
                    <Portrait name={l.fullName} src={l.photograph} size="md" />
                    <h3 className="mt-6 font-serif text-xl text-navy-900 group-hover:text-gold-dark transition-colors">{l.fullName}</h3>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">{l.designation}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal>
              <p className="mt-8 max-w-xl rounded-2xl border border-dashed border-navy-900/20 bg-ivory px-8 py-10 text-sm leading-relaxed text-slate-600" data-testid="brand-leadership-empty">
                Leadership profiles for {brand.brandName} will be published here as they are announced.
              </p>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-navy-950 py-24" data-testid="brand-cta">
        <div className="container-rcs flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="max-w-2xl font-serif text-3xl text-ivory sm:text-4xl">
              Discover the complete {brand.brandName} experience.
            </h2>
            <p className="mt-4 max-w-xl text-base text-slate-400">
              Programmes, admissions and learning details live on the official {brand.brandName} website.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-wrap gap-4">
              <a href={brand.websiteURL} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="brand-footer-visit-cta">
                Visit Official Website <ArrowUpRight className="h-4 w-4" aria-hidden />
              </a>
              <Link to="/brands" className="btn-outline-light" data-testid="brand-back-cta">
                All RCS Brands <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
